import {
  COPILOT_QUERY_PARAM,
  COPILOT_PROMPT,
} from "../components/TryTemplateButton/TryTemplateButton";

type ExecuteTemplateOptions = {
  templateSlug: string;
  shouldUseTemplateAPIExecute: boolean;
  url?: string;
  apiHostName?: string;
};

export const useExecuteTemplate = () => {
  const executeTemplate = async ({
    templateSlug,
    shouldUseTemplateAPIExecute,
    url = window.location.href,
    apiHostName,
  }: ExecuteTemplateOptions) => {
    try {
      const resp = await fetch(`${apiHostName || ""}/api/templates/execute`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          templateSlug,
          url,
          shouldUseTemplateAPIExecute,
        }),
        credentials: "include",
      });

      const body = await resp.json();
      if (!resp.ok) {
        const status = resp.status;
        const error = body.errors
          ? body.errors[0]
          : body.responseBody?.errors?.[0];
        if (error?.code === "service:unknown") {
          throw new Error(
            `Failed to execute (status: ${status}) ${error.error}`,
          );
        }
        throw error || new Error(`HTTP ${status}`);
      }

      const redirectTo = new URL(body.redirectTo, window.location.origin);

      redirectTo.searchParams.set(COPILOT_QUERY_PARAM, COPILOT_PROMPT);

      window.location.href = redirectTo.href;
    } catch (error) {
      console.error("Template execution error:", error);
      throw error;
    }
  };

  const DEFAULT_ERROR_MESSAGE =
    "Something went wrong while trying to use this template.";

  const formatExecutionError = (error: {
    code: string;
    status: string;
    title: string;
    detail: string;
  }) => {
    switch (error.code) {
      case "tables:limits:too_many_tables":
      case "limits:too_many_tables": {
        return `You have reached the limit of Tables for your plan. Please upgrade your plan to create more.`;
      }
      case "limits:projects":
      case "interfaces:limits:projects": {
        return `You have reached the limit of Interfaces for your plan. Please upgrade your plan to create more.`;
      }
      case "limits:chatbots":
      case "chatbots:limits:chatbots": {
        return `You have reached the limit of Chatbots for your plan. Please upgrade your plan to create more.`;
      }
      case "upstream-error": {
        if (
          error.title === "GulliverApiError" &&
          (error.status === "426" || error.status === "402")
        ) {
          return "This template contains Zap(s) that includes features (steps, paths, premium apps) not covered by your plan. Please upgrade your plan to use this template.";
        }
        return DEFAULT_ERROR_MESSAGE;
      }

      default: {
        return DEFAULT_ERROR_MESSAGE;
      }
    }
  };

  return { executeTemplate, formatExecutionError };
};
